import React from 'react';
import { motion } from 'framer-motion';

interface TeamPerformanceChartProps {
  taskStats: Record<string, number>;
  priorityStats: Record<string, number>;
}

const TeamPerformanceChart: React.FC<TeamPerformanceChartProps> = ({ taskStats, priorityStats }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-500';
      case 'IN_PROGRESS':
        return 'bg-blue-500';
      case 'TODO':
        return 'bg-gray-500';
      case 'CANCELLED':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-500';
      case 'MEDIUM':
        return 'bg-yellow-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const totalTasks = Object.values(taskStats).reduce((sum, count) => sum + count, 0);
  const totalPriorityTasks = Object.values(priorityStats).reduce((sum, count) => sum + count, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Task Status Chart */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Task Status Distribution</h3>
        <div className="space-y-4">
          {Object.entries(taskStats).map(([status, count]) => {
            const percentage = totalTasks > 0 ? (count / totalTasks) * 100 : 0;
            return (
              <div key={status} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded ${getStatusColor(status)}`} />
                  <span className="text-sm font-medium text-gray-700">
                    {status.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <motion.div
                      className={`h-2 rounded-full ${getStatusColor(status)}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-12 text-right">
                    {count} ({percentage.toFixed(0)}%)
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Priority Distribution Chart */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Priority Distribution</h3>
        <div className="space-y-4">
          {Object.entries(priorityStats).map(([priority, count]) => {
            const percentage = totalPriorityTasks > 0 ? (count / totalPriorityTasks) * 100 : 0;
            return (
              <div key={priority} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded ${getPriorityColor(priority)}`} />
                  <span className="text-sm font-medium text-gray-700">
                    {priority} Priority
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <motion.div
                      className={`h-2 rounded-full ${getPriorityColor(priority)}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-12 text-right">
                    {count} ({percentage.toFixed(0)}%)
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TeamPerformanceChart;
